import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommunicationBridge } from '@sequelae/lore-mfe-kit';
import { Button, Typography } from '@sequelae/lore-core';
import { WheelDatePicker } from '../../components/WheelDatePicker';
import { postMessageService } from '../../services/postMessage';
import { useNavigationMode } from '../../contexts/NavigationModeContext';
import { NavigationModeToggle } from '../../components/NavigationModeToggle';
import { NavigationTestButtons } from '../../components/NavigationTestButtons';
import AccountService from '../../services/AccountService';
import type { AccountData, DemographicUpdate } from '../../services/AccountService';
import VerificationService from '../../services/VerificationService';
import type { VerificationData } from '../../services/VerificationService';
import '../../styles/accountInfo.css';
import '../../styles/shared-styles.css';
import { getRefUserId } from '../../utils/getRefUserId';

interface FormData extends Omit<AccountData, 'phone'> {
  phoneNumber: string;
  useForShipping: boolean;
  shippingName: string;
  streetAddress: string;
  addressLine2: string;
  city: string;
  state: string;
  zipcode: string;
}

export const AccountInformation = () => {
  const navigate = useNavigate();
  const { isNativeMode } = useNavigationMode();

  // Get ref_user_id priority: URL > localStorage > token > default
  // Note: App.tsx handles props and token, but AccountInformation may be called directly
  const urlParams = new URLSearchParams(window.location.search);
  const urlUserId = urlParams.get('ref_user_id');
  const localStorageUserId = window.localStorage.getItem('ref_user_id');
  const tokenUserId = getRefUserId();

  const refUserId = urlUserId
    ? parseInt(urlUserId, 10)
    : localStorageUserId
    ? parseInt(localStorageUserId, 10)
    : tokenUserId
    ? tokenUserId
    : 44016;

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [verificationData, setVerificationData] = useState<VerificationData | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    phoneNumber: '',
    useForShipping: true,
    shippingName: '',
    streetAddress: '',
    addressLine2: '',
    city: '',
    state: '',
    zipcode: '',
  });

  // Convert YYYY-MM-DD from API to MMM DD YYYY for display
  const convertApiDateToDisplay = useCallback((apiDate: string) => {
    if (!apiDate) return '';
    try {
      // Extract date part if it's an ISO string
      const datePart = apiDate.split('T')[0];
      const date = new Date(datePart + 'T00:00:00Z');
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = monthNames[date.getUTCMonth()];
      const day = date.getUTCDate();
      const year = date.getUTCFullYear();
      return `${month} ${day} ${year}`;
    } catch (err) {
      console.error('Error converting API date:', err);
      return apiDate;
    }
  }, []);

  // Convert MMM DD YYYY to MM/DD/YYYY for the date picker
  const convertDisplayToPickerFormat = useCallback((displayDate: string) => {
    if (!displayDate) return '';
    try {
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const parts = displayDate.split(' ');
      if (parts.length !== 3) return displayDate;
      const monthIndex = monthNames.indexOf(parts[0]);
      if (monthIndex === -1) return displayDate;
      const month = (monthIndex + 1).toString().padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${month}/${day}/${year}`;
    } catch (err) {
      console.error('Error converting display date to picker format:', err);
      return displayDate;
    }
  }, []);

  // Convert MMM DD YYYY to YYYY-MM-DD for the API
  const convertDisplayToApiFormat = useCallback((displayDate: string) => {
    if (!displayDate) return '';
    try {
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const parts = displayDate.split(' ');
      if (parts.length !== 3) return displayDate;
      const monthIndex = monthNames.indexOf(parts[0]);
      if (monthIndex === -1) return displayDate;
      const month = (monthIndex + 1).toString().padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    } catch (err) {
      console.error('Error converting display date to API format:', err);
      return displayDate;
    }
  }, []);

  // Convert MM/DD/YYYY from picker to MMM DD YYYY for display
  const convertPickerToDisplayFormat = useCallback((pickerDate: string) => {
    if (!pickerDate) return '';
    try {
      const [month, day, year] = pickerDate.split('/');
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const monthName = monthNames[parseInt(month) - 1];
      return `${monthName} ${parseInt(day)} ${year}`;
    } catch (err) {
      console.error('Error converting picker date to display format:', err);
      return pickerDate;
    }
  }, []);

  // Fetch account data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        console.log('[MenuMFE] Fetching account data for user:', refUserId);
        const data = await AccountService.fetchAccountData(refUserId);
        console.log('[MenuMFE] Account data fetched successfully:', data);

        // Map API data to form fields
        setFormData(prev => ({
          ...prev,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          middleName: data.middleName || '',
          dateOfBirth: convertApiDateToDisplay(data.dateOfBirth || ''),
          phoneNumber: data.phone || '',
          useForShipping: true,
          shippingName: `${data.firstName || ''} ${data.lastName || ''}`.trim(),
        }));
      } catch (err) {
        console.error('[MenuMFE] Failed to fetch account data:', err);
        setError('Unable to load account data. You can still enter your information.');
        // Keep the form empty but don't prevent rendering
        setFormData({
          firstName: '',
          middleName: '',
          lastName: '',
          dateOfBirth: '',
          phoneNumber: '',
          useForShipping: true,
          shippingName: '',
          streetAddress: '',
          addressLine2: '',
          city: '',
          state: '',
          zipcode: '',
        });
      }
    };

    fetchData();
  }, [refUserId, convertApiDateToDisplay]);

  // Fetch verification data on mount
  useEffect(() => {
    const fetchVerificationData = async () => {
      try {
        console.log('[MenuMFE] Fetching verification data');
        const data = await VerificationService.getVerificationStatus();
        console.log('[MenuMFE] Verification data fetched:', data);
        setVerificationData(data);
      } catch (err) {
        console.error('[MenuMFE] Failed to fetch verification data:', err);
        // Don't show error - verification data is optional
      }
    };

    fetchVerificationData();
  }, []);

  // Get email and username from JWT token
  useEffect(() => {
    const token = CommunicationBridge.getAccessToken();
    if (!token) return;

    try {
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
      setUserEmail(decodedToken.email || decodedToken.emails?.[0] || '');
      setUserName(decodedToken.user_name || '');
    } catch (e) {
      console.error('Error decoding token:', e);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const detailsPanel = document.querySelector('.details-panel');
      const detailsButton = document.querySelector('.details-button');
      if (
        showDetails &&
        detailsPanel &&
        detailsButton &&
        !detailsPanel.contains(event.target as Node) &&
        !detailsButton.contains(event.target as Node)
      ) {
        setShowDetails(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDetails]);

  const handleChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'useForShipping' && value === true
        ? {
            shippingName: `${formData.firstName} ${formData.lastName}`.trim(),
          }
        : {}),
    }));

    // If it's a demographic field, debounce the update
    if (['firstName', 'lastName', 'middleName', 'dateOfBirth', 'phoneNumber'].includes(field)) {
      setSaveStatus('saving');

      // Clear any existing timeout to debounce
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Debounce the API call - wait 1 second after user stops typing
      saveTimeoutRef.current = setTimeout(() => {
        // Convert dateOfBirth from display format (MMM DD YYYY) to API format (YYYY-MM-DD)
        const dateForApi =
          field === 'dateOfBirth'
            ? convertDisplayToApiFormat(value as string)
            : convertDisplayToApiFormat(formData.dateOfBirth);

        const demographics: DemographicUpdate[] = [
          {
            ref_demographic_id: 1,
            ref_user_id: refUserId,
            demographic_value: field === 'firstName' ? (value as string) : formData.firstName,
          },
          {
            ref_demographic_id: 2,
            ref_user_id: refUserId,
            demographic_value: field === 'lastName' ? (value as string) : formData.lastName,
          },
          {
            ref_demographic_id: 3,
            ref_user_id: refUserId,
            demographic_value: field === 'middleName' ? (value as string) : formData.middleName,
          },
          {
            ref_demographic_id: 7,
            ref_user_id: refUserId,
            demographic_value: dateForApi,
          },
          {
            ref_demographic_id: 10,
            ref_user_id: refUserId,
            demographic_value: field === 'phoneNumber' ? (value as string) : formData.phoneNumber,
          },
        ];

        AccountService.updateAccount(demographics, refUserId)
          .then(() => {
            setSaveStatus('saved');
            // Reset to idle after 2 seconds
            saveTimeoutRef.current = setTimeout(() => {
              setSaveStatus('idle');
            }, 2000);
          })
          .catch((err: Error) => {
            console.error('Failed to update account:', err);
            setError(err instanceof Error ? err.message : 'Failed to update account');
            setSaveStatus('idle');
          });
      }, 1000); // Wait 1 second after user stops typing
    }
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const numbers = value.replace(/\D/g, '');

    // Don't format if backspacing or empty
    if (numbers.length === 0) return '';

    // Format as (XXX) XXX-XXXX
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const formatted = formatPhoneNumber(input);
    handleChange('phoneNumber', formatted);
  };

  // Format verification birth date from YYYY-MM-DD or ISO format to MMM DD YYYY
  const formatVerificationDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    try {
      // Extract date part if it's an ISO string (remove time portion)
      const datePart = dateString.split('T')[0];
      // Create a date object (add time to ensure UTC interpretation)
      const date = new Date(datePart + 'T00:00:00Z');
      // Format as MMM DD YYYY (e.g., "Jun 15 1978")
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = monthNames[date.getUTCMonth()];
      const day = date.getUTCDate();
      const year = date.getUTCFullYear();
      return `${month} ${day} ${year}`;
    } catch (err) {
      console.error('Error formatting verification date:', err);
      return dateString;
    }
  };

  return (
    <div className="screen">
      <div className="shared-container">
        <div className="account-info-container android">
          <header className="account-header">
            {!isNativeMode && (
              <Button className="back-button" onClick={() => navigate(-1)} variant="text">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z"
                    fill="#1C1B1F"
                  />
                </svg>
              </Button>
            )}
            <Typography variant="h1" className="account-header-title">
              Account Information
            </Typography>
            {saveStatus !== 'idle' && (
              <Typography
                variant="body"
                className={`ml-auto font-medium ${
                  saveStatus === 'saving' ? 'text-secondary' : 'text-success'
                }`}
              >
                {saveStatus === 'saving' ? 'Saving...' : 'Saved ✓'}
              </Typography>
            )}
            <NavigationModeToggle />
          </header>
          <div className="account-form">
            {error && (
              <div className="error-message text-error p-2 mb-2">
                <Typography variant="body" color="error">
                  {error}
                </Typography>
              </div>
            )}

            <div className="form-section">
              <Typography
                variant="h2"
                className="section-title"
                style={{
                  marginTop: 'var(--spacing-space-8)',
                  marginBottom: 'var(--spacing-space-8)',
                }}
              >
                Account information
              </Typography>
              <div className="form-group">
                <label className="form-label active">
                  <Typography variant="caption">Email</Typography>
                </label>
                <input
                  type="email"
                  className="form-input"
                  value={userEmail}
                  readOnly
                  disabled
                  aria-label="Email (read-only)"
                />
              </div>
              <div className="form-group">
                <label className="form-label active">
                  <Typography variant="caption">Username</Typography>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={userName}
                  readOnly
                  disabled
                  aria-label="Username (read-only)"
                />
              </div>
            </div>

            <div className="form-section mt-8">
              <Typography
                variant="h2"
                className="section-title"
                style={{
                  marginTop: 'var(--spacing-space-8)',
                  marginBottom: 'var(--spacing-space-8)',
                }}
              >
                Personal information
              </Typography>
            </div>
            <div className="form-group">
              <label className={formData.firstName ? 'form-label active' : 'form-label'}>
                <Typography variant="caption">First name</Typography>
              </label>
              <input
                type="text"
                placeholder="First name"
                className="form-input"
                value={formData.firstName}
                onChange={e => handleChange('firstName', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className={formData.middleName ? 'form-label active' : 'form-label'}>
                <Typography variant="caption">Middle name</Typography>
              </label>
              <input
                type="text"
                placeholder="Middle name"
                className="form-input"
                value={formData.middleName}
                onChange={e => handleChange('middleName', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className={formData.lastName ? 'form-label active' : 'form-label'}>
                <Typography variant="caption">Last name</Typography>
              </label>
              <input
                type="text"
                placeholder="Last name"
                className="form-input"
                value={formData.lastName}
                onChange={e => handleChange('lastName', e.target.value)}
              />
            </div>
            <div className="form-group">
              <div className="date-input-container">
                <input
                  type="text"
                  placeholder="MMM DD YYYY"
                  className="form-input cursor-pointer"
                  value={formData.dateOfBirth}
                  readOnly
                  onClick={() => setShowDatePicker(true)}
                />
                <span className={`input-label ${formData.dateOfBirth ? 'active' : ''}`}>
                  <Typography variant="caption">Date of birth</Typography>
                </span>
                <Button
                  type="button"
                  className="date-picker-trigger"
                  onClick={() => setShowDatePicker(true)}
                  variant="text"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V10H19V20ZM19 8H5V6H19V8Z"
                      fill="#1C1B1F"
                    />
                  </svg>
                </Button>
              </div>
            </div>
            <div className="form-group">
              <label className={formData.phoneNumber ? 'form-label active' : 'form-label'}>
                <Typography variant="caption">Phone number</Typography>
              </label>
              <input
                type="tel"
                placeholder="(555) 555-5555"
                className="form-input"
                value={formData.phoneNumber}
                onChange={handlePhoneChange}
                maxLength={14}
              />
            </div>

            <div className="checkbox-group mt-6 mb-4">
              <input
                type="checkbox"
                id="use-for-shipping"
                checked={formData.useForShipping}
                onChange={e => handleChange('useForShipping', e.target.checked)}
              />
              <label htmlFor="use-for-shipping">
                <Typography variant="body">Use this name for shipping address</Typography>
              </label>
            </div>

            <div className="form-section">
              <Typography
                variant="h2"
                className="section-title"
                style={{
                  marginTop: 'var(--spacing-space-8)',
                  marginBottom: 'var(--spacing-space-8)',
                }}
              >
                Shipping address
              </Typography>
              {!formData.useForShipping && (
                <div className="form-group">
                  <label className={formData.shippingName ? 'form-label active' : 'form-label'}>
                    <Typography variant="caption">Name</Typography>
                  </label>
                  <input
                    type="text"
                    placeholder="Name"
                    className="form-input"
                    value={formData.shippingName}
                    onChange={e => handleChange('shippingName', e.target.value)}
                  />
                </div>
              )}
              <div className="form-group">
                <label className={formData.streetAddress ? 'form-label active' : 'form-label'}>
                  <Typography variant="caption">Street address</Typography>
                </label>
                <input
                  type="text"
                  placeholder="Street address"
                  className="form-input"
                  value={formData.streetAddress}
                  onChange={e => handleChange('streetAddress', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className={formData.addressLine2 ? 'form-label active' : 'form-label'}>
                  <Typography variant="caption">Apt, suite, bldg. (optional)</Typography>
                </label>
                <input
                  type="text"
                  placeholder="Apt, suite, bldg. (optional)"
                  className="form-input"
                  value={formData.addressLine2}
                  onChange={e => handleChange('addressLine2', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className={formData.city ? 'form-label active' : 'form-label'}>
                  <Typography variant="caption">City</Typography>
                </label>
                <input
                  type="text"
                  placeholder="City"
                  className="form-input"
                  value={formData.city}
                  onChange={e => handleChange('city', e.target.value)}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className={formData.state ? 'form-label active' : 'form-label'}>
                    <Typography variant="caption">State</Typography>
                  </label>
                  <input
                    type="text"
                    placeholder="State"
                    className="form-input"
                    value={formData.state}
                    onChange={e => handleChange('state', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className={formData.zipcode ? 'form-label active' : 'form-label'}>
                    <Typography variant="caption">Zipcode</Typography>
                  </label>
                  <input
                    type="text"
                    placeholder="Zipcode"
                    className="form-input"
                    value={formData.zipcode}
                    onChange={e => handleChange('zipcode', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="form-section mt-8">
              <Typography
                variant="h2"
                className="section-title"
                style={{
                  marginTop: 'var(--spacing-space-8)',
                  marginBottom: 'var(--spacing-space-8)',
                }}
              >
                Verification information
              </Typography>
              <div className="form-group">
                <label className="form-label active" htmlFor="verification-first-name">
                  <Typography variant="caption">First name</Typography>
                </label>
                <input
                  id="verification-first-name"
                  type="text"
                  className="form-input"
                  value={verificationData?.input_first_name || ''}
                  disabled
                  readOnly
                  aria-label="Verification first name (read-only)"
                />
              </div>
              <div className="form-group">
                <label className="form-label active" htmlFor="verification-last-name">
                  <Typography variant="caption">Last name</Typography>
                </label>
                <input
                  id="verification-last-name"
                  type="text"
                  className="form-input"
                  value={verificationData?.input_last_name || ''}
                  disabled
                  readOnly
                  aria-label="Verification last name (read-only)"
                />
              </div>
              <div className="form-group">
                <label className="form-label active" htmlFor="verification-dob">
                  <Typography variant="caption">Date of birth</Typography>
                </label>
                <input
                  id="verification-dob"
                  type="text"
                  className="form-input"
                  value={
                    verificationData
                      ? formatVerificationDate(verificationData.input_birth_date)
                      : ''
                  }
                  disabled
                  readOnly
                  aria-label="Verification date of birth (read-only)"
                />
              </div>
              <div className="form-group">
                <label className="form-label active" htmlFor="verification-email">
                  <Typography variant="caption">Email address</Typography>
                </label>
                <input
                  id="verification-email"
                  type="text"
                  className="form-input"
                  value={verificationData?.input_email || ''}
                  disabled
                  readOnly
                  aria-label="Verification email address (read-only)"
                />
              </div>
            </div>
          </div>
          <div className="navigation-test-spacer">
            <NavigationTestButtons />
          </div>
        </div>
      </div>
      {showDatePicker && (
        <WheelDatePicker
          value={convertDisplayToPickerFormat(formData.dateOfBirth)}
          onChange={value => {
            handleChange('dateOfBirth', convertPickerToDisplayFormat(value));
            setShowDatePicker(false);
          }}
          onClose={() => setShowDatePicker(false)}
        />
      )}
    </div>
  );
};
